import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  SelectedTagIdsType,
  useCreateCustomTagMutation,
  useDeleteCustomTagsMutation,
  useGetCustomTagsQuery,
  useUpdateCustomTagsMutation,
} from '../../api/metric-groups';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { Tag } from '../../types/metricGroup';
import { BulkActionUtr } from '@g17eco/types/survey-question-list';
import { AddTagList } from './AddTagList';
import { ManageTags } from './ManageTags';
import './style.scss';
import { BasicAlertParams } from '@g17eco/molecules/alert';

export interface TagContainerProps {
  selectedQuestions: BulkActionUtr[];
  handleReload: (props?: { reloadSurvey?: boolean }) => Promise<void | undefined>;
  initiativeId?: string;
}

export interface Message {
  content: string;
  type: BasicAlertParams['type'];
}

export const TagContainer = (props: TagContainerProps) => {
  const { handleReload, selectedQuestions } = props;
  const { initiativeId: routeInitiativeId = '' } = useParams<{ initiativeId: string }>();
  const initiativeId = props.initiativeId ?? routeInitiativeId;

  const { data: tags = [], error, isFetching } = useGetCustomTagsQuery(initiativeId, { skip: !initiativeId });
  const [updateCustomTags, { isLoading: isUpdating }] = useUpdateCustomTagsMutation();
  const [createCustomTag] = useCreateCustomTagMutation();
  const [deleteCustomTags] = useDeleteCustomTagsMutation();

  const [isManageTag, setManageTag] = useState(false);
  const [message, setMessage] = useState<Message | undefined>();

  const selectedUtrIds = useMemo(() => {
    return selectedQuestions.map((question) => question._id);
  }, [selectedQuestions]);

  const addMessage = (content: string, type: BasicAlertParams['type'] = 'success') => {
    setMessage({ content, type });
    setTimeout(() => {
      setMessage(undefined);
    }, 2000);
  };

  const handleAddTag = (tag: Tag['groupName']) => {
    return createCustomTag({ initiativeId, groupName: tag })
      .then(() => {
        addMessage(`${tag} has been added`);
      })
      .catch((e) => {
        addMessage(e.message, 'danger');
      });
  };

  const handleDeleteTags = (ids: Tag['_id'][]) => {
    deleteCustomTags({ initiativeId, ids })
      .then(() => {
        addMessage('Tags have been deleted successfully!');
      })
      .catch((e) => {
        addMessage(e.message, 'danger');
      });
  };

  const handleAddUtrsToTags = (selectedTagIds: SelectedTagIdsType) => {
    updateCustomTags({ initiativeId, updatedTagIds: selectedTagIds, utrIds: selectedUtrIds })
      .unwrap()
      .then(() => {
        generateToast({ color: 'success', message: 'Tags updated successfully' });
        handleReload({ reloadSurvey: true });
      })
      .catch((error) => {
        toastFromError({ error, title: 'Failed to update tags' });
        handleReload();
      });
  };

  if (isManageTag) {
    return (
      <ManageTags
        tags={tags}
        initiativeId={initiativeId}
        message={message}
        handleAddTag={handleAddTag}
        handleDeleteTags={handleDeleteTags}
        toggleManageTag={() => setManageTag(false)}
      />
    );
  }
  return (
    <AddTagList
      tags={tags}
      isLoading={isFetching || isUpdating}
      tagListError={error}
      selectedUtrIds={selectedUtrIds}
      openManageTags={() => setManageTag(true)}
      handleAddUtrsToTags={handleAddUtrsToTags}
    />
  );
};
